/**
 * Edge renderer — renders connections between clusters.
 * Edges are hidden by default and fade in on hover/selection.
 *
 * Uses LineSegments with per-line opacity for selective visibility.
 */

import * as THREE from 'three';
import type { SyntheticEdge, SyntheticNode } from '../data/synthetic';

export class EdgeRenderer {
  public lines: THREE.LineSegments;
  private opacities: Float32Array;
  private opacityAttr: THREE.BufferAttribute;

  constructor(nodes: SyntheticNode[], edges: SyntheticEdge[]) {
    const positions = new Float32Array(edges.length * 6); // 2 points × 3 coords
    this.opacities = new Float32Array(edges.length * 2); // 1 opacity per vertex

    edges.forEach((edge, i) => {
      const src = nodes[edge.source];
      const tgt = nodes[edge.target];
      if (!src || !tgt) return;

      positions[i * 6 + 0] = src.position[0];
      positions[i * 6 + 1] = src.position[1];
      positions[i * 6 + 2] = src.position[2];
      positions[i * 6 + 3] = tgt.position[0];
      positions[i * 6 + 4] = tgt.position[1];
      positions[i * 6 + 5] = tgt.position[2];

      // Start invisible
      this.opacities[i * 2] = 0;
      this.opacities[i * 2 + 1] = 0;
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.opacityAttr = new THREE.BufferAttribute(this.opacities, 1);
    geometry.setAttribute('opacity', this.opacityAttr);

    const material = new THREE.ShaderMaterial({
      vertexShader: `
        attribute float opacity;
        varying float vOpacity;
        void main() {
          vOpacity = opacity;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying float vOpacity;
        void main() {
          if (vOpacity < 0.01) discard;
          gl_FragColor = vec4(0.6, 0.6, 0.7, vOpacity * 0.25);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    this.lines = new THREE.LineSegments(geometry, material);
  }

  /**
   * Show edges connected to a specific node index.
   */
  showEdgesForNode(nodeIndex: number, edges: SyntheticEdge[], targetOpacity: number = 1.0) {
    edges.forEach((edge, i) => {
      if (edge.source === nodeIndex || edge.target === nodeIndex) {
        this.opacities[i * 2] = targetOpacity * edge.weight;
        this.opacities[i * 2 + 1] = targetOpacity * edge.weight;
      }
    });
    this.opacityAttr.needsUpdate = true;
  }

  /**
   * Hide all edges.
   */
  hideAll() {
    this.opacities.fill(0);
    this.opacityAttr.needsUpdate = true;
  }

  dispose() {
    this.lines.geometry.dispose();
    (this.lines.material as THREE.Material).dispose();
  }
}
